import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchAllMetrics } from '../utils/api';
import { calculateCompositeScore, getStatus, THRESHOLDS } from '../utils/riskCalculator';
import { useHistoricalData } from './useLocalStorage';

/**
 * Main hook for fetching and managing all metrics
 * @param {number} refreshInterval - Polling interval in milliseconds
 * @param {Object} portfolioData - User portfolio configuration
 * @returns {Object} Metrics state and control functions
 */
export function useMetrics(refreshInterval = 60000, portfolioData = {}) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isStale, setIsStale] = useState(false);

  // Historical data storage
  const utilizationHistory = useHistoricalData('utilization-history');
  const pegHistory = useHistoricalData('peg-history');
  const tvlHistory = useHistoricalData('tvl-history');
  const fundingRateHistory = useHistoricalData('funding-rate-history');

  const intervalRef = useRef(null);

  // Fetch metrics function
  const fetchMetrics = useCallback(async () => {
    try {
      setError(null);
      const data = await fetchAllMetrics();
      
      setMetrics(data);
      setLastUpdate(Date.now());
      setIsStale(false);
      setLoading(false);

      // Store historical data
      if (data.utilization !== null) {
        utilizationHistory.addDataPoint(data.utilization);
      }
      if (data.peg?.deviation !== null) {
        pegHistory.addDataPoint(data.peg.deviation);
      }
      if (data.tvl?.current !== null) {
        tvlHistory.addDataPoint(data.tvl.current);
      }
      if (data.fundingRate !== null) {
        fundingRateHistory.addDataPoint(data.fundingRate);
      }

      return data;
    } catch (err) {
      // Don't log rate limit errors as "errors" - they're expected
      if (err.message && !err.message.includes('Rate limit exceeded')) {
        console.error('Error fetching metrics:', err);
        setError(err.message || 'Failed to fetch metrics');
      } else {
        // Rate limited - this is normal, just use cached data
        console.info('ℹ️  Rate limited - displaying cached data');
      }
      setLoading(false);
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty to prevent infinite loops

  // Initial fetch
  useEffect(() => {
    fetchMetrics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty to prevent infinite loops

  // Polling setup
  useEffect(() => {
    if (refreshInterval > 0) {
      intervalRef.current = setInterval(fetchMetrics, refreshInterval);
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshInterval]); // Intentionally missing fetchMetrics to prevent infinite loops

  // Check for stale data
  useEffect(() => {
    const staleCheckInterval = setInterval(() => {
      if (lastUpdate) {
        const age = Date.now() - lastUpdate;
        const stalenessThreshold = portfolioData.stalenessThreshold || 5; // Default 5 minutes
        setIsStale(age > stalenessThreshold * 60 * 1000); // Stale if > threshold
      }
    }, 30000); // Check every 30s

    return () => clearInterval(staleCheckInterval);
  }, [lastUpdate, portfolioData.stalenessThreshold]);

  // Calculate composite score and statuses
  const enrichedMetrics = metrics ? {
    ...metrics,
    compositeScore: calculateCompositeScore(metrics, portfolioData),
    statuses: {
      utilization: getStatus(metrics.utilization, THRESHOLDS.utilization),
      pegDeviation: getStatus(metrics.peg?.deviation, THRESHOLDS.pegDeviation),
      loopExposure: getStatus(metrics.loopExposure, THRESHOLDS.loopExposure),
      fundingRate: getStatus(metrics.fundingRate, THRESHOLDS.fundingRate),
      tvlChange: getStatus(metrics.tvl?.change24h, THRESHOLDS.tvlChange)
    },
    history: {
      utilization: utilizationHistory.data,
      peg: pegHistory.data,
      tvl: tvlHistory.data,
      fundingRate: fundingRateHistory.data
    }
  } : null;

  // Manual refresh function
  const refresh = useCallback(() => {
    setLoading(true);
    return fetchMetrics();
  }, [fetchMetrics]);

  return {
    metrics: enrichedMetrics,
    loading,
    error,
    lastUpdate,
    isStale,
    refresh
  };
}

/**
 * Hook for monitoring specific metric thresholds and triggering alerts
 * @param {Object} metrics - Current metrics object
 * @param {Function} onAlert - Callback function when threshold is breached
 */
export function useAlerts(metrics, onAlert) {
  const previousMetrics = useRef(null);
  const alertCooldowns = useRef({});

  useEffect(() => {
    if (!metrics || !previousMetrics.current) {
      previousMetrics.current = metrics;
      return;
    }

    const prev = previousMetrics.current;
    const alerts = [];
    const now = Date.now();
    const COOLDOWN_PERIOD = 5 * 60 * 1000; // 5 minutes cooldown

    // Check each metric for status changes to red or yellow
    const metricsToCheck = [
      { name: 'utilization', current: metrics.utilization, previous: prev.utilization },
      { name: 'pegDeviation', current: metrics.peg?.deviation, previous: prev.peg?.deviation },
      { name: 'loopExposure', current: metrics.loopExposure, previous: prev.loopExposure },
      { name: 'fundingRate', current: metrics.fundingRate, previous: prev.fundingRate },
      { name: 'tvlChange', current: metrics.tvl?.change24h, previous: prev.tvl?.change24h }
    ];

    metricsToCheck.forEach(({ name, current, previous }) => {
      if (current === null || current === undefined) return;

      const currentStatus = metrics.statuses?.[name];
      const threshold = THRESHOLDS[name];
      const prevStatus = previous !== null && previous !== undefined 
        ? getStatus(previous, threshold)
        : 'green';

      // Create cooldown key
      const cooldownKey = `${name}-${currentStatus}`;
      const lastAlertTime = alertCooldowns.current[cooldownKey] || 0;
      
      // Check if cooldown period has passed
      const canAlert = (now - lastAlertTime) > COOLDOWN_PERIOD;

      // Alert on status degradation, but respect cooldown
      if (canAlert) {
        if (currentStatus === 'red' && prevStatus !== 'red') {
          alerts.push({
            metric: name,
            value: current,
            status: 'red',
            timestamp: now
          });
          alertCooldowns.current[cooldownKey] = now;
        } else if (currentStatus === 'yellow' && prevStatus === 'green') {
          alerts.push({
            metric: name,
            value: current,
            status: 'yellow',
            timestamp: now
          });
          alertCooldowns.current[cooldownKey] = now;
        }
      }
    });

    // Trigger callback for each alert
    if (alerts.length > 0 && onAlert) {
      alerts.forEach(alert => onAlert(alert));
    }

    previousMetrics.current = metrics;
  }, [metrics, onAlert]);
}

/**
 * Hook for calculating time since last update
 * @param {number} lastUpdate - Timestamp of last update
 * @returns {string} Human-readable time string
 */
export function useTimeSince(lastUpdate) {
  const [timeSince, setTimeSince] = useState('');

  useEffect(() => {
    const updateTimeSince = () => {
      if (!lastUpdate) {
        setTimeSince('Never');
        return;
      }

      const seconds = Math.floor((Date.now() - lastUpdate) / 1000);
      
      if (seconds < 60) {
        setTimeSince(`${seconds}s ago`);
      } else if (seconds < 3600) {
        setTimeSince(`${Math.floor(seconds / 60)}m ago`);
      } else {
        setTimeSince(`${Math.floor(seconds / 3600)}h ago`);
      }
    };

    updateTimeSince();
    const interval = setInterval(updateTimeSince, 1000);

    return () => clearInterval(interval);
  }, [lastUpdate]);

  return timeSince;
}

